import { useContext, useEffect, useRef } from "react";

import { DockPanelKeyContext } from "~/providers/DockPanelContext";

export type ConfirmedExitKind = "navigation" | "unload";

interface UseUnsavedChangesGuardArgs {
	enabled: boolean;
	holdsExit?: boolean;
	keepsEntriesInDock?: boolean;
	message?: string;
	takeConfirmedExit?: (kind: ConfirmedExitKind) => boolean;
}

interface ArmedGuard {
	holdsExit: () => boolean;
	keepsEntriesInDock: boolean;
	message: () => string;
	panelKey: string;
	spendConfirmedExit: (kind: ConfirmedExitKind) => boolean;
}

const DEFAULT_MESSAGE = "You have unsaved row edits. Leave anyway?";

const armedGuards: ArmedGuard[] = [];
const lastGuardDisarmListeners = new Set<() => void>();
let unguardedPushState: History["pushState"];
let unguardedReplaceState: History["replaceState"];
let lastConfirmedUrl = "";
let lastConfirmedState: unknown = null;
let isClosingConfirmedPanels = false;
const resolveNoPage = (): string | undefined => undefined;
let resolvePageOfPath: (pathname: string) => string | undefined = resolveNoPage;

export const registerGuardPageResolver = (resolvePage: (pathname: string) => string | undefined): (() => void) => {
	resolvePageOfPath = resolvePage;
	return () => {
		resolvePageOfPath = resolveNoPage;
	};
};

const pageOf = (pathname: string): string => resolvePageOfPath(pathname) ?? pathname;

const pathnameOf = (url: string | URL | null | undefined): string =>
	new URL(url ?? window.location.href, window.location.href).pathname;

const keepsTheCurrentPage = (url: string | URL | null | undefined): boolean =>
	pageOf(pathnameOf(url)) === pageOf(window.location.pathname);

export const isDockPagePath = (url: string | URL): boolean => resolvePageOfPath(pathnameOf(url)) !== undefined;

const asksBeforeLeavingFor = (guard: ArmedGuard, url: string | URL | null | undefined): boolean =>
	!guard.keepsEntriesInDock || !isDockPagePath(url ?? window.location.href);

const answersLeave = (guard: ArmedGuard): boolean => {
	if (!guard.holdsExit()) return window.confirm(guard.message());
	window.alert(guard.message());
	return false;
};

const recordConfirmedLocation = () => {
	lastConfirmedUrl = window.location.href;
	lastConfirmedState = window.history.state;
};

const isNavigationAllowed = (url: string | URL | null | undefined): boolean => {
	if (isClosingConfirmedPanels) return true;
	const askingGuards = [...armedGuards]
		.reverse()
		.filter((guard) => asksBeforeLeavingFor(guard, url) && !guard.spendConfirmedExit("navigation"));
	const answeringGuard = askingGuards.find((guard) => guard.holdsExit()) ?? askingGuards[0];
	return !answeringGuard || answersLeave(answeringGuard);
};

const guardedPushState: History["pushState"] = (state, unused, url) => {
	if (!isNavigationAllowed(url)) return;
	unguardedPushState.call(window.history, state, unused, url);
	recordConfirmedLocation();
};

const guardedReplaceState: History["replaceState"] = (state, unused, url) => {
	if (!keepsTheCurrentPage(url) && !isNavigationAllowed(url)) return;
	unguardedReplaceState.call(window.history, state, unused, url);
	recordConfirmedLocation();
};

const handlePopState = () => {
	const isAnyGuardArmed = armedGuards.length > 0;
	const isReentryFromOurRestore = window.location.href === lastConfirmedUrl;
	if (!isAnyGuardArmed || isReentryFromOurRestore) return;
	const askingGuards = armedGuards.filter((guard) => asksBeforeLeavingFor(guard, window.location.href));
	const answeringGuard = askingGuards.find((guard) => guard.holdsExit()) ?? askingGuards[0];
	if (!answeringGuard || answersLeave(answeringGuard)) {
		recordConfirmedLocation();
		return;
	}
	unguardedPushState.call(window.history, lastConfirmedState, "", lastConfirmedUrl);
	window.dispatchEvent(new PopStateEvent("popstate", { state: lastConfirmedState }));
};

export const installBackGuardBeforeRouter = () => {
	window.addEventListener("popstate", handlePopState);
};

const armGuard = (guard: ArmedGuard) => {
	if (armedGuards.length === 0) {
		unguardedPushState = window.history.pushState;
		unguardedReplaceState = window.history.replaceState;
		recordConfirmedLocation();
		window.history.pushState = guardedPushState;
		window.history.replaceState = guardedReplaceState;
		installBackGuardBeforeRouter();
	}
	armedGuards.push(guard);

	return () => {
		armedGuards.splice(armedGuards.indexOf(guard), 1);
		if (armedGuards.length > 0) return;
		window.history.pushState = unguardedPushState;
		window.history.replaceState = unguardedReplaceState;
		lastGuardDisarmListeners.forEach((listener) => listener());
	};
};

export const isAnyGuardArmed = (): boolean => armedGuards.length > 0;

export const subscribeToLastGuardDisarm = (listener: () => void): (() => void) => {
	lastGuardDisarmListeners.add(listener);
	return () => {
		lastGuardDisarmListeners.delete(listener);
	};
};

export const hasUnsavedChangesInPanel = (panelKey: string): boolean =>
	armedGuards.some((guard) => guard.panelKey === panelKey);

export const confirmDiscardThenClose = (panelKeys: readonly string[], close: () => void): void => {
	const closingGuards = armedGuards.filter((guard) => panelKeys.includes(guard.panelKey));
	if (closingGuards.length > 0) {
		const answeringGuard = closingGuards.find((guard) => guard.holdsExit()) ?? closingGuards[closingGuards.length - 1];
		if (!answersLeave(answeringGuard)) return;
		// dockview activates a neighbour tab inside close(), and the dock navigates to it before close() returns.
		isClosingConfirmedPanels = true;
	}
	try {
		close();
	} finally {
		isClosingConfirmedPanels = false;
	}
};

// Nav guard for <BrowserRouter>: useBlocker is data-router-only and would throw here,
// so we intercept the History API (what navigate() calls), beforeunload, and popstate.
const useUnsavedChangesGuard = ({
	enabled,
	holdsExit = false,
	keepsEntriesInDock = false,
	message = DEFAULT_MESSAGE,
	takeConfirmedExit
}: UseUnsavedChangesGuardArgs): void => {
	const messageRef = useRef(message);
	messageRef.current = message;
	const holdsExitRef = useRef(holdsExit);
	holdsExitRef.current = holdsExit;
	const takeConfirmedExitRef = useRef(takeConfirmedExit);
	takeConfirmedExitRef.current = takeConfirmedExit;
	const panelKey = useContext(DockPanelKeyContext);

	useEffect(() => {
		if (!enabled) return;

		const spendConfirmedExit = (kind: ConfirmedExitKind) => takeConfirmedExitRef.current?.(kind) === true;

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (spendConfirmedExit("unload")) return undefined;
			event.preventDefault();
			event.returnValue = messageRef.current;
			return messageRef.current;
		};

		// react-router dispatches pushState/replaceState through the live window.history
		// object, so patching its methods in place catches every navigate() (incl. MUI Buttons).
		const disarmThisGuard = armGuard({
			holdsExit: () => holdsExitRef.current,
			keepsEntriesInDock,
			message: () => messageRef.current,
			panelKey,
			spendConfirmedExit
		});
		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			disarmThisGuard();
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [enabled, keepsEntriesInDock, panelKey]);
};

export default useUnsavedChangesGuard;
